/**
 * ProjectSelector (SK-HOME-1, T-20, DS-1g-05 §5.1/§8, R-PROJECT-CONTEXT) —
 * AC-1g-07.
 *
 * Populated EXCLUSIVELY from the provisioned RBAC scope `project_ids`
 * (§7.2) — pick-don't-type, NEVER inferred/created (TW-1g-PROJECT-INFER).
 * Sticky per session (the caller persists `selected` across the App's
 * session state; this component itself is stateless about persistence —
 * it reports selection changes via `onSelect` and is handed its current
 * value, matching the controlled-component convention used by every other
 * component this batch adds).
 *
 * `project_ref` on EVERY emitted Event comes from here — enforced by
 * construction: `selectedProjectRef()` is the ONLY way this module
 * exposes a project_ref value, and it can only ever return one of the
 * exact strings in `project_ids` (never a typed-in or synthesized one).
 *
 * Design reconciliation (Minimal Dark Mode "1 Record" + "8 Switch Project"):
 * the resting state is a compact PILL (amber dot + the current project name).
 * Tapping the pill opens the Switch-Project popover (a floating card anchored
 * under the pill, per `Mark PWA Preview.dc.html` — NOT a bottom sheet) holding
 * the RBAC-scoped option list. While NO project is selected the popover is
 * FORCED open (the super must pick before any capture routes), so the option
 * list is present on first render exactly as before.
 *
 * A SHORT TAP used to do nothing at all: opening was a deliberate 500ms long
 * press, on the reasoning that switching project mid-day should not be casual.
 * On device that read as a broken control — Red, field test 2026-07-29: the pill
 * names the current project, looks like a button, and swallowed every tap with
 * no hint that a press-and-hold was wanted. It is also the app's own "never a
 * dead tap" rule, broken by the one control that has to work before anything
 * else can.
 *
 * The deliberateness is kept where it actually belongs: opening the popover
 * changes NOTHING, and switching still costs a second, aimed tap on a named
 * project. The long press is retained as an alias so existing muscle memory
 * still lands.
 */

import { useRef, useState } from "preact/hooks";
import { Chip } from "./Chip";
import { MenuRow } from "./MenuRow";
import { ModalCard } from "./Modal";

const LONG_PRESS_MS = 500;

export interface ProjectSelectorProps {
  project_ids: string[];
  selected: string | null;
  onSelect: (project_ref: string) => void;
}

/** The single accessor for "the current project_ref" — returns `selected`
 * ONLY if it is a member of `project_ids` (defends against a stale
 * selection surviving a re-provision that shrank the scope), else `null`.
 * Every emission-composing task (T-11/T-21/etc.) is expected to read the
 * project_ref through this function, not by trusting `selected` directly. */
export function selectedProjectRef(project_ids: string[], selected: string | null): string | null {
  if (selected !== null && project_ids.includes(selected)) return selected;
  return null;
}

export function ProjectSelector({ project_ids, selected, onSelect }: ProjectSelectorProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Forced open while nothing is picked (first-run: no capture routes without
  // a project); otherwise a tap on the pill opens it.
  const open = pickerOpen || selected === null;
  const pillLabel = selected ?? "Select a project";

  function handlePillPointerDown() {
    longPressTimer.current = setTimeout(() => setPickerOpen(true), LONG_PRESS_MS);
  }
  function clearLongPressTimer() {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  return (
    <section data-skin-slot="SK-HOME-1" data-testid="project-selector" style={{ position: "relative", display: "flex", justifyContent: "center" }}>
      <Chip
        data-testid="project-selector-pill"
        aria-haspopup="true"
        aria-expanded={open}
        // OPEN, not toggle. A long press fires the timer and THEN emits a click
        // on release, so a toggle here would open the popover and shut it again
        // in the same gesture. Closing is the popover's own `close`, or picking.
        onClick={() => setPickerOpen(true)}
        onPointerDown={handlePillPointerDown}
        onPointerUp={clearLongPressTimer}
        onPointerLeave={clearLongPressTimer}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: "inherit",
          userSelect: "none",
          WebkitUserSelect: "none",
        }}
      >
        <span
          aria-hidden="true"
          style={{ width: "8px", height: "8px", borderRadius: "50%", background: "var(--mark-accent-amber)", flex: "none" }}
        />
        <span
          style={{
            fontSize: "10.5px",
            fontWeight: 600,
            letterSpacing: "0.8px",
            textTransform: "uppercase",
            color: "var(--mark-text-muted)",
          }}
        >
          {pillLabel}
        </span>
        {/* The hint the field test asked for: a control that opens something has
            to look like one. Sized/coloured to sit under the label rather than
            compete with it — the pill stays the quiet chrome it was designed as. */}
        <span
          aria-hidden="true"
          style={{ fontSize: "9px", color: "var(--mark-text-hint)", flex: "none", lineHeight: 1 }}
        >
          {"▾"}
        </span>
      </Chip>

      {open && (
        <ModalCard
          testid="project-selector-picker"
          style={{ position: "absolute", top: "52px", left: "50%", transform: "translateX(-50%)", zIndex: 10 }}
          width="300px"
          padding="16px 20px"
          shadow
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingBottom: "8px" }}>
            <span style={{ fontSize: "16px", fontWeight: 700, color: "var(--mark-text-primary)" }}>Switch project</span>
            <button
              type="button"
              data-testid="project-selector-close"
              onClick={() => setPickerOpen(false)}
              style={{ background: "none", border: "none", minHeight: 0, padding: 0, fontFamily: "inherit", fontSize: "11px", fontWeight: 500, color: "var(--mark-text-muted)", cursor: "pointer" }}
            >
              close
            </button>
          </div>
          <ul data-testid="project-selector-list" style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
            {project_ids.map((id) => (
              <li key={id} style={{ borderTop: "1px solid var(--mark-bg-surface-alt)" }}>
                <MenuRow
                  size="lg"
                  muted={id !== selected}
                  leading={id === selected && (
                    <span
                      aria-hidden="true"
                      style={{ width: "8px", height: "8px", borderRadius: "50%", background: "var(--mark-accent-amber)", flex: "none" }}
                    />
                  )}
                  data-testid={`project-selector-option-${id}`}
                  aria-current={id === selected ? "true" : undefined}
                  onClick={() => {
                    onSelect(id);
                    setPickerOpen(false);
                  }}
                >
                  {id}
                </MenuRow>
              </li>
            ))}
          </ul>
          <span style={{ fontSize: "11px", color: "var(--mark-text-faint)", display: "block", paddingTop: "8px" }}>
            Only jobs assigned to you are shown.
          </span>
        </ModalCard>
      )}
    </section>
  );
}
